import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useState } from "react";

const FONT_SIZE = "0.875rem";

const SectionHeader = ({ label, total, open, onToggle }) => (
  <TableRow
    sx={{ backgroundColor: "grey.100", cursor: "pointer" }}
    onClick={onToggle}
  >
    <TableCell colSpan={6}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={700}
          color={"text.secondary"}
        >
          {label}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          {total != null && (
            <Typography
              fontSize={FONT_SIZE}
              fontWeight={600}
              color={"text.secondary"}
            >
              {numbers.currencyFormat(total, 0)}
            </Typography>
          )}
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          )}
        </Stack>
      </Stack>
    </TableCell>
  </TableRow>
);

const CollapsibleSection = ({ open, children }) => (
  <TableRow sx={{ p: 0 }}>
    <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>{children}</TableBody>
        </Table>
      </Collapse>
    </TableCell>
  </TableRow>
);

const HeadRow = () => (
  <TableRow sx={{ backgroundColor: "grey.50" }}>
    <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}>
      {"Weight & Type"}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 100 }}
    >
      {"Min Rate"}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 120 }}
    >
      {"Count"}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 120 }}
    >
      {"Guideline Rate"}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 130 }}
    >
      {"Selected Rate ($)"}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 100 }}
    >
      {"Selected / Guide (%)"}
    </TableCell>
  </TableRow>
);

const inputSx = {
  width: "100%",
  "& .MuiInputBase-input": {
    fontSize: FONT_SIZE,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};

const VehicleRow = ({ row, selectedRate, onUpdateRate }) => {
  const isPpt = row.key === "vehiclePpt" && row.ppt_equivalent > 0;
  const countLabel = isPpt
    ? `${numbers.dpString(0)(row.raw_count)} + ${numbers.dpString(2)(
        row.ppt_equivalent
      )} HNOA`
    : numbers.dpString(2)(row.count);

  const effectiveRate = selectedRate ?? row.guideline_rate;
  const selGuidePct =
    row.guideline_rate > 0 ? (effectiveRate / row.guideline_rate) * 100 : 100;

  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: FONT_SIZE }}>
        <Tooltip
          title={
            isPpt
              ? `Incl. ${numbers.dpString(2)(
                  row.ppt_equivalent
                )} PPT-equivalent (HNOA)`
              : ""
          }
          placement={"top"}
        >
          <span>{row.label}</span>
        </Tooltip>
      </TableCell>
      <TableCell align={"right"} sx={{ fontSize: FONT_SIZE }}>
        {numbers.currencyFormat(row.base_rate, 0)}
      </TableCell>
      <TableCell
        align={"right"}
        sx={{
          fontSize: FONT_SIZE,
          color: isPpt ? "text.secondary" : "text.primary",
        }}
      >
        {countLabel}
      </TableCell>
      <TableCell align={"right"} sx={{ fontSize: FONT_SIZE }}>
        {numbers.currencyFormat(row.guideline_rate, 0)}
      </TableCell>
      <TableCell sx={{ py: 0.25 }}>
        <PrettyNumberTextField
          hiddenLabel
          size={"small"}
          variant={"filled"}
          value={effectiveRate}
          onChangeNumber={(v) => onUpdateRate(row.key, v ?? row.guideline_rate)}
          sx={inputSx}
        />
      </TableCell>
      <TableCell sx={{ py: 0.25 }}>
        <PrettyNumberTextField
          hiddenLabel
          size={"small"}
          variant={"filled"}
          value={selGuidePct}
          onChangeNumber={(v) =>
            onUpdateRate(
              row.key,
              v != null ? row.guideline_rate * (v / 100) : row.guideline_rate
            )
          }
          sx={inputSx}
        />
      </TableCell>
    </TableRow>
  );
};

const CascadeRow = ({ label, value, bold, color }) => (
  <TableRow>
    <TableCell
      colSpan={4}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 400,
        color: color ?? (bold ? "text.primary" : "text.secondary"),
        border: 0,
        py: 0.5,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      colSpan={2}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 600,
        color: color ?? "text.primary",
        border: 0,
        py: 0.5,
      }}
    >
      {value}
    </TableCell>
  </TableRow>
);

const MarketInputRow = ({ label, value, onChange }) => (
  <TableRow>
    <TableCell
      colSpan={4}
      align={"right"}
      sx={{ fontSize: FONT_SIZE, color: "text.secondary", border: 0, py: 0.25 }}
    >
      {label}
    </TableCell>
    <TableCell colSpan={2} sx={{ border: 0, py: 0.25 }}>
      <PrettyNumberTextField
        hiddenLabel
        size={"small"}
        variant={"filled"}
        value={value ?? null}
        onChangeNumber={onChange}
        sx={inputSx}
      />
    </TableCell>
  </TableRow>
);

const AutoPricingResults = ({
  values,
  selectedRates,
  onUpdateRate,
  data,
  onUpdate,
  proRataFactor = 1,
}) => {
  const [open, setOpen] = useState({ vehicles: true, market: true });
  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!values) return null;

  const { records = [], totals = {}, inputs = {} } = values;
  const activeRecords = records.filter((r) => r.count > 0);

  const selectedTotal = activeRecords.reduce((sum, row) => {
    const rate = selectedRates?.[row.key] ?? row.guideline_rate;
    return sum + row.count * rate;
  }, 0);
  const selectedPremium = Math.round(selectedTotal);
  const proRatedPremium = Math.round(selectedTotal * proRataFactor);

  return (
    <TableContainer>
      <Table size={"small"} sx={{ tableLayout: "fixed" }}>
        <TableBody>
          <SectionHeader
            label={"Vehicle Breakdown"}
            total={selectedPremium}
            open={open.vehicles}
            onToggle={() => toggle("vehicles")}
          />
          <CollapsibleSection open={open.vehicles}>
            <HeadRow />
            {activeRecords.map((row) => (
              <VehicleRow
                key={row.key}
                row={row}
                selectedRate={selectedRates?.[row.key] ?? null}
                onUpdateRate={onUpdateRate}
              />
            ))}
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  pt: 1,
                }}
              >
                {[
                  `State: ${inputs.state ?? "—"}`,
                  `Usage: ${inputs.usage ?? "—"}`,
                  `Setting: ${inputs.setting ?? "—"}`,
                  `Hazard: ${inputs.auto_hazard_grade ?? "—"}`,
                  `Factor: ${numbers.dpString(4)(totals.combined_factor)}`,
                ].join("  ·  ")}
              </TableCell>
              <TableCell
                colSpan={2}
                align={"right"}
                sx={{
                  fontSize: FONT_SIZE,
                  fontWeight: 700,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  pt: 1,
                }}
              >
                {numbers.currencyFormat(selectedPremium, 0)}
              </TableCell>
            </TableRow>
          </CollapsibleSection>

          <SectionHeader
            label={"Market Summary"}
            open={open.market}
            onToggle={() => toggle("market")}
          />
          <CollapsibleSection open={open.market}>
            <MarketInputRow
              label={"Primary Auto Market Charged Premium ($)"}
              value={data?.primaryAutoMarketChargedPremium}
              onChange={(v) => onUpdate("primaryAutoMarketChargedPremium", v)}
            />
            <MarketInputRow
              label={"Primary Auto Market Limit ($)"}
              value={data?.primaryAutoMarketLimit}
              onChange={(v) => onUpdate("primaryAutoMarketLimit", v)}
            />
            <CascadeRow
              label={
                proRataFactor !== 1
                  ? `Exposure Rating Premium (${numbers.dpString(3)(
                      proRataFactor * 100
                    )}% pro rata)`
                  : "Exposure Rating Premium"
              }
              value={numbers.currencyFormat(proRatedPremium, 0)}
              bold
            />
          </CollapsibleSection>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AutoPricingResults;
